/* eslint-disable id-length */
import { yupResolver } from '@hookform/resolvers/yup';
import { MIME_TYPES } from '@mantine/dropzone';
import { useQueryClient } from '@tanstack/react-query';
import { GetEmployerDto } from '@zorro/clients';
import {
  ERROR_MESSAGES,
  UNEXPECTED_ERROR_MESSAGE,
  VALIDATION_MESSAGES,
} from '@zorro/common-utils';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Link } from '@zorro/design-system/v2/Link';
import { Pill, PillColor } from '@zorro/design-system/v2/Pill';
import { getNowAsDate } from '@zorro/shared/formatters';
import { DeepReadonly } from '@zorro/types';
import {
  DataTable,
  FormErrorMessage,
  Grid,
  LabelWithTooltip,
  Space,
  Text,
} from '@zorro/web/design-system';
import {
  clearAllNotifications,
  extractCustomClassCategories,
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
  useAppRouter,
  useForm,
  useLoadingOverlay,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import csv from 'csvtojson';
import { useMemo, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import * as XLSX from 'xlsx';
import * as yup from 'yup';

import { FileUploader } from '../FileUploader';
import { FormFooter } from '../FormFooter';
import { UploadedFileBox } from '../UploadedFileBox';
import {
  INCORRECT_FILE_FORMAT_MESSAGE,
  createEmployeesFromRosterUpload,
  toRosterUploadIssues,
} from './CreateEmployeesFromRosterUploadUtils';
import { RosterInfoAlert } from './RosterInfoAlert';
import { getHeaderCompatibleToCsv, isValidRosterSheet } from './rosterTemplate';
import {
  RosterInput,
  RosterIssueSeverity,
  RosterUploadIssue,
} from './uploadRoster.types';

export const uploadRosterSchema = yup.object({
  rosterFile: yup
    .mixed<File>()
    .required(VALIDATION_MESSAGES.rosterFileRequired),
  sendActivationEmail: yup.boolean().required(),
});

export type UploadRosterFormFields = yup.InferType<typeof uploadRosterSchema>;

const ROSTER_ISSUE_PILLS: DeepReadonly<{
  [severity in RosterIssueSeverity]: { color: PillColor; label: string };
}> = {
  error: { color: 'red', label: 'Error' },
  warning: { color: 'orange', label: 'Warning' },
};

const emptyRow = (row: RosterInput) => {
  return Object.values(row).every(
    (value) => !value || String(value).trim() === ''
  );
};

export type UploadRosterProps = {
  employerId: GetEmployerDto['id'];
  onSuccess: () => void;
};

export function UploadRosterForm({ employerId, onSuccess }: UploadRosterProps) {
  'use no memo';
  const { startLoading, stopLoading } = useLoadingOverlay();
  const {
    handleSubmit,
    control,
    watch,
    resetField,
    formState: { isValid, isSubmitting, errors },
  } = useForm<UploadRosterFormFields>({
    mode: 'all',
    resolver: yupResolver(uploadRosterSchema),
    defaultValues: {
      rosterFile: undefined,
      sendActivationEmail: true,
    },
  });
  const { reloadPage } = useAppRouter();
  const queryClient = useQueryClient();

  const [uploadIssues, setUploadIssues] = useState<RosterUploadIssue[]>([]);

  const handleRosterFileRemoval = () => {
    resetField('rosterFile');
  };

  const { data: { classDefinitions } = { classDefinitions: [] } } =
    useMonolithQuery({
      method: 'allowanceModelControllerGetAllClassDefinitionsForEmployer',
      params: [employerId],
    });

  const now = getNowAsDate();
  const currentYear = now.getFullYear();
  const combinedClassDefinitions = extractCustomClassCategories(
    classDefinitions,
    currentYear,
    true
  );

  const uploadRoster = async ({
    rosterFile,
    sendActivationEmail,
  }: UploadRosterFormFields) => {
    setUploadIssues([]);
    clearAllNotifications();
    if (!rosterFile) {
      showErrorNotification({
        message: ERROR_MESSAGES.NO_FILE_SELECTED_ERROR_MESSAGE,
      });
      return;
    }
    startLoading();
    let rosterTextContent: string;

    if (rosterFile.name.toLowerCase().endsWith('.xlsx')) {
      const workbook = XLSX.read(await rosterFile.arrayBuffer(), {
        cellText: false,
        cellDates: true,
      });
      const sheetIdx = Math.max(
        workbook.SheetNames.findIndex((name) =>
          name.toLowerCase().includes('roster')
        ),
        0
      );
      const sheet = workbook.Sheets[workbook.SheetNames[sheetIdx]];
      if (!isValidRosterSheet(sheet)) {
        showErrorNotification({
          message: INCORRECT_FILE_FORMAT_MESSAGE,
        });
        stopLoading();
        return;
      }

      // In XSLX we have 2 header rows. We skip one, and change the other to be compatible to our CSV format
      const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
      const headerCompatibleToCsv = getHeaderCompatibleToCsv();
      headerCompatibleToCsv.forEach((header, index) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 1, c: index });
        sheet[cellAddress] = { t: 's', v: header };
      });
      range.s.r = 1;
      sheet['!ref'] = XLSX.utils.encode_range(range);
      rosterTextContent = XLSX.utils.sheet_to_csv(sheet, {
        blankrows: false,
        skipHidden: true,
        dateNF: 'MM-DD-YYYY',
      });
    } else if (rosterFile.name.toLowerCase().endsWith('.csv')) {
      rosterTextContent = await rosterFile.text();
    } else {
      throw new Error('Invalid file format');
    }

    const roster = (
      (await csv({ eol: '\n' }).fromString(rosterTextContent)) as RosterInput[]
    ).filter((row) => !emptyRow(row));

    if (roster.length === 0) {
      stopLoading();
      showErrorNotification({
        message: ERROR_MESSAGES.NO_DATA_FOUND_IN_THE_FILE_ERROR_MESSAGE,
      });
      return;
    }

    const classesToUse = combinedClassDefinitions || [];

    const uploadRosterResult = await createEmployeesFromRosterUpload(
      employerId,
      roster,
      sendActivationEmail,
      classesToUse,
      [currentYear, currentYear + 1]
    );
    setUploadIssues(toRosterUploadIssues(uploadRosterResult));
    if (uploadRosterResult.success) {
      if (uploadRosterResult.errors.length === 0) {
        if (uploadRosterResult.warnings.length === 0) {
          onSuccess();
        }

        showSuccessNotification({
          message:
            uploadRosterResult.message || 'Roster uploaded successfully!',
          title: 'Success!',
          id: 'roster-upload-success-notification',
        });
      } else {
        showWarningNotification({
          message: uploadRosterResult.message,
          title: 'Warning!',
          id: 'roster-upload-warning-notification',
        });
      }
    } else {
      showErrorNotification({
        message: uploadRosterResult.message || UNEXPECTED_ERROR_MESSAGE,
        title: 'Oh no!',
        id: 'roster-upload-fail-notification',
      });
    }
    stopLoading();
    setTimeout(async () => {
      await queryClient.invalidateQueries();
    }, 0);
    reloadPage();
  };

  const rosterFileField = watch('rosterFile');

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Upload"
        primaryButtonProps={{
          onClick: handleSubmit(uploadRoster),
          disabled: !isValid || isSubmitting,
        }}
      />
    ),
    [handleSubmit, uploadRoster, isValid, isSubmitting]
  );
  useDrawerFooter(drawerFooter);

  return (
    <>
      <RosterInfoAlert
        items={combinedClassDefinitions ?? []}
        label="custom class categories"
      />

      <form onSubmit={handleSubmit(uploadRoster)}>
        {!rosterFileField && (
          <FileUploader
            control={control as unknown as Control}
            name="rosterFile"
            label="Upload roster"
            topRightContent={
              <Link
                href="/files/sample_roster.xlsx"
                download="sample_roster.xlsx"
              >
                Download roster template
              </Link>
            }
            onDrop={(files: File[], onBlur, onChange) => {
              onChange(files[0]);
              onBlur();
            }}
            isRequired
            isMultiple={false}
            isDisabled={!!rosterFileField}
            accept={[MIME_TYPES.csv, MIME_TYPES.xlsx]}
            zoneLabelText="Drag file here or click to select it"
            buttonText="Upload roster"
          />
        )}
        {rosterFileField && (
          <UploadedFileBox
            onClickDelete={handleRosterFileRemoval}
            name={rosterFileField.name}
            label="Upload roster"
            isRequired
          />
        )}
        <Space h="sm" />
        <FormErrorMessage fieldName="rosterFile" errors={errors} />
        <Controller
          control={control}
          name="sendActivationEmail"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              my="sm"
              ml="xs"
              checked={value}
              label={
                <LabelWithTooltip
                  label="Send invitation to all new employees"
                  tooltipLabel="Employees need an invitation to set up their Zorro account and log in for the first time"
                />
              }
            />
          )}
        />
        <Space h="sm" />
      </form>

      {uploadIssues.length > 0 && (
        <>
          <Space h="xl" />

          <Grid gap="xl">
            <Grid.Col>
              <Text c="zorroFire.7" size="md" fw="bold" my="sm">
                Errors and warnings
              </Text>
              <DataTable
                columns={[
                  {
                    accessor: 'severity',
                    title: 'Type',
                    render: ({ severity }: RosterUploadIssue) => {
                      const { color, label } = ROSTER_ISSUE_PILLS[severity];
                      return (
                        <Pill variant="tag" color={color}>
                          {label}
                        </Pill>
                      );
                    },
                  },
                  {
                    accessor: 'employeeDetails',
                    title: 'Employee name',
                    render: ({ employeeDetails }) =>
                      `${employeeDetails.firstName} ${employeeDetails.lastName}`,
                  },
                  { accessor: 'message' },
                ]}
                records={uploadIssues}
                scrollAreaProps={{ offsetScrollbars: 'present' }}
              />
            </Grid.Col>
          </Grid>
        </>
      )}
    </>
  );
}
